import { triggerEvent } from './util/event-handler';

export type ThemeValue = 'light' | 'dark';

const EVENT_CHANGE = 'antumo:theme:change';
const EVENT_CHANGED = 'antumo:theme:changed';

const ATTR_THEME = 'data-theme';
const STORAGE_KEY = 'antumo-theme';
const DARK_MQ = '(prefers-color-scheme: dark)';

/**
 * Static utility for managing light/dark theme.
 *
 * Operates on `document.documentElement` (`<html>` element).
 * Persists choice in `localStorage` and syncs across tabs.
 *
 * Usage:
 *   Theme.set('dark');
 *   Theme.toggle();
 *   Theme.current;              // 'dark' | 'light'
 *   Theme.useSystemPreference(); // follow OS, clear manual override
 *   Theme.init();                // restore saved or system preference
 */
export class Theme {
  private static _mqListener: ((e: MediaQueryListEvent) => void) | null = null;
  private static _storageListener: ((e: StorageEvent) => void) | null = null;
  private static _initialized = false;

  static get current(): ThemeValue {
    if (typeof document === 'undefined') return 'light';
    return document.documentElement.getAttribute(ATTR_THEME) === 'dark'
      ? 'dark'
      : 'light';
  }

  /**
   * Set theme explicitly. Persists to localStorage and dispatches events.
   *
   * Events:
   *   `antumo:theme:change`  — cancelable, detail: `{ from, to }`
   *   `antumo:theme:changed` — not cancelable, detail: `{ theme }`
   */
  static set(theme: ThemeValue): void {
    if (typeof document === 'undefined') return;

    const from = Theme.current;
    if (from === theme) return;

    const event = triggerEvent(
      document.documentElement,
      EVENT_CHANGE,
      { from, to: theme },
      true,
    );
    if (event.defaultPrevented) return;

    Theme._applyTheme(theme);
    Theme._savePreference(theme);
    Theme._removeSystemListener();

    triggerEvent(
      document.documentElement,
      EVENT_CHANGED,
      { theme },
      false,
    );
  }

  static toggle(): void {
    Theme.set(Theme.current === 'dark' ? 'light' : 'dark');
  }

  /**
   * Follow operating system preference. Clears any manual override
   * from localStorage so the theme tracks `prefers-color-scheme`.
   */
  static useSystemPreference(): void {
    if (typeof document === 'undefined') return;

    Theme._clearPreference();

    const prefersDark = Theme._systemPrefersDark();
    const target = prefersDark ? 'dark' : 'light';
    const from = Theme.current;

    if (from !== target) {
      triggerEvent(
        document.documentElement,
        EVENT_CHANGE,
        { from, to: target },
        false, // not cancelable — system preference is authoritative
      );

      Theme._applyTheme(target);

      triggerEvent(
        document.documentElement,
        EVENT_CHANGED,
        { theme: target },
        false,
      );
    }

    Theme._listenToSystemChanges();
  }

  /** Restore saved preference or system default. Starts cross-tab sync.
   *  Respects an existing data-theme attribute set by the server. */
  static init(): void {
    if (typeof document === 'undefined') return;
    if (Theme._initialized) return;
    Theme._initialized = true;

    const saved = Theme._loadPreference();

    if (saved === 'dark' || saved === 'light') {
      Theme._applyTheme(saved);
    }
    // No saved preference — stay light (default). Apps opt into dark mode
    // via Theme.set('dark') or Theme.useSystemPreference().

    Theme._listenToStorageChanges();
  }

  static dispose(): void {
    Theme._removeSystemListener();
    Theme._removeStorageListener();
    Theme._initialized = false;
  }

  // ── Private helpers ──────────────────────────────────────────────

  private static _applyTheme(theme: ThemeValue): void {
    if (theme === 'dark') {
      document.documentElement.setAttribute(ATTR_THEME, 'dark');
    } else {
      document.documentElement.removeAttribute(ATTR_THEME);
    }
  }

  private static _savePreference(theme: ThemeValue): void {
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // localStorage unavailable — degrade gracefully
    }
  }

  private static _loadPreference(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private static _clearPreference(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // localStorage unavailable — degrade gracefully
    }
  }

  private static _systemPrefersDark(): boolean {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(DARK_MQ).matches;
  }

  private static _listenToSystemChanges(): void {
    if (typeof window === 'undefined') return;
    Theme._removeSystemListener();

    const mq = window.matchMedia(DARK_MQ);
    Theme._mqListener = (e: MediaQueryListEvent) => {
      // Only react if there is no manual override
      const saved = Theme._loadPreference();
      if (saved !== null) return;

      const target: ThemeValue = e.matches ? 'dark' : 'light';
      const from = Theme.current;
      if (from === target) return;

      triggerEvent(
        document.documentElement,
        EVENT_CHANGE,
        { from, to: target },
        false,
      );

      Theme._applyTheme(target);

      triggerEvent(
        document.documentElement,
        EVENT_CHANGED,
        { theme: target },
        false,
      );
    };
    mq.addEventListener('change', Theme._mqListener);
  }

  private static _removeSystemListener(): void {
    if (typeof window === 'undefined' || !Theme._mqListener) return;
    window.matchMedia(DARK_MQ).removeEventListener('change', Theme._mqListener);
    Theme._mqListener = null;
  }

  private static _listenToStorageChanges(): void {
    if (typeof window === 'undefined') return;
    Theme._removeStorageListener();

    Theme._storageListener = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;

      const newTheme = e.newValue;
      if (newTheme === 'dark' || newTheme === 'light') {
        Theme._applyTheme(newTheme);
        triggerEvent(
          document.documentElement,
          EVENT_CHANGED,
          { theme: newTheme },
          false,
        );
      } else {
        // Preference removed — fall back to system
        const prefersDark = Theme._systemPrefersDark();
        Theme._applyTheme(prefersDark ? 'dark' : 'light');
      }
    };
    window.addEventListener('storage', Theme._storageListener);
  }

  private static _removeStorageListener(): void {
    if (typeof window === 'undefined' || !Theme._storageListener) return;
    window.removeEventListener('storage', Theme._storageListener);
    Theme._storageListener = null;
  }
}
